<template>
    <div v-if="isLoading" class="loading-wrapper">
        <img
            class="spinner"
            src="/apps/assets/img/ajax-spinner.gif"
            alt="Spinning loader"
        />
    </div>

    <ul v-else-if="results.length > 0">
        <li v-for="(result, index) in results" :key="index">
            <router-link
                data-testid="result-item"
                class="option-link"
                :to="{ name: 'item', params: { id: result.id } }"
                @click.native="$emit('result-click')"
            >
                <module-icon name="inventory" class="icon" />
                <div class="d-flex flex-column flex-grow-1">
                    <div class="d-flex">
                        <span class="fw-medium flex-grow-1">
                            {{ result.name }}
                        </span>
                        <i
                            v-if="result.uses_count > 0"
                            class="fas fa-sitemap"
                        ></i>
                    </div>
                    <span class="module">Inventory</span>
                </div>
            </router-link>
        </li>
    </ul>

    <p v-else-if="searchInput.trim() !== ''" class="no-data">
        No matches found for "<span class="fw-medium">{{ searchInput }}</span
        >"
    </p>
</template>

<script>
import ModuleIcon from "@/module/layout/module-icon";
import { debounce } from "lodash";

const INVENTORY_SEARCH_URI = `${process.env.BARRAMUNDI}inventory/search`;

export default {
    name: "TabPanelInventory",
    components: { ModuleIcon },
    props: {
        searchInput: {
            type: String
        }
    },
    data() {
        return {
            isLoading: false,
            lastSearchedQuery: "",
            requestId: 0,
            results: []
        };
    },
    watch: {
        searchInput() {
            this.handleSearch();
        }
    },
    created() {
        this.debouncedSearch = debounce(this.fetchResult, 350);
    },
    mounted() {
        if (this.searchInput) {
            this.handleSearch();
        }
    },
    methods: {
        handleSearch() {
            const currentRequestId = ++this.requestId;

            const query = this.searchInput.trim();
            this.isLoading = query !== "";

            if (query === "") {
                this.results = [];
                return;
            }

            this.debouncedSearch(query, currentRequestId);
        },
        async fetchResult(query, currentRequestId) {
            const res = await this.$http.get(INVENTORY_SEARCH_URI, {
                params: {
                    query: query,
                    page: 1
                }
            });

            const { message, total, ...data } = res.data.response;

            if (message.code === 200) {
                if (currentRequestId !== this.requestId) {
                    return;
                }
                this.results = Object.values(data);
                this.isLoading = false;
            } else {
                this.results = [];
                this.isLoading = false;
            }
        }
    },
    beforeDestroy() {
        if (this.debouncedSearch && this.debouncedSearch.cancel) {
            this.debouncedSearch.cancel();
        }
    }
};
</script>

<style lang="scss" scoped>
$vet-text-subtlest: #626f86;
$vet-background-neutral-subtle-hovered: #091e420f;
$vet-border-focused: #388bff;

ul {
    list-style-type: none;
    padding: 0;
}

a.option-link {
    display: flex;
    gap: 8px;
    width: 100%;
    padding: 6px 24px 4px;
    background-color: transparent;
    text-align: start;
    font-weight: unset;
    color: unset;

    &:focus-visible {
        outline: 2px solid $vet-border-focused;
        outline-offset: -2px;
    }

    &:hover {
        background-color: $vet-background-neutral-subtle-hovered;
    }

    .icon {
        width: 18px;
        height: 18px;
        margin-top: 1px;
        flex-shrink: 0;
    }

    .module {
        font-size: 12px;
        font-weight: 400;
        color: $vet-text-subtlest;
    }
}

.fw-medium {
    font-weight: 500;
}

.loading-wrapper {
    display: flex;
    justify-content: center;
    padding: 16px 20px 0 20px;
}

.no-data {
    padding: 8px 20px 0 20px;
}
</style>
